/*
 * export.js — turn a /pdf-editor session into PDF bytes. Exactly once.
 *
 * The whole point of the session model is that a user can rotate, reorder,
 * annotate, sign and redact without the document being re-encoded between
 * each step. This module is where that single encode happens.
 *
 * Coordinates are NOT re-derived here. Placements are page fractions and are
 * projected into MediaBox space by public/js/pdf-coords.js — the transform
 * already proven by /sign-pdf and locked by tests/js/pdf-coords.test.mjs. A
 * second copy of that math is how the two tools would drift apart.
 *
 * Text is drawn as REAL TEXT via public/js/pdf-text-font.js: run-split so a
 * mixed-script line gets a covering face per run, and subset so a line of
 * Cyrillic costs ~8 KB instead of ~300 KB. Rasterising text (what /sign-pdf
 * does) is available only as an explicit, user-visible fallback — never as a
 * silent one, because a stamp that cannot be selected or searched is a
 * quality downgrade the user did not ask for.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfEditorExport = api;
})(typeof window !== 'undefined' ? window : null, function () {
    'use strict';

    /* Dependencies are injected rather than reached for through globals, so the
       whole export path is exercisable in node (tests/js/pdf-editor-export). */
    function makeExporter(deps) {
        var PDFLib = deps.PDFLib;              // pdf-lib namespace
        var Coords = deps.PdfCoords;
        var Fonts = deps.PdfTextFont;
        var degrees = PDFLib.degrees;
        var rgbFn = PDFLib.rgb;

        function hexToRgb(hex) {
            var h = String(hex || '#000000').replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            var n = parseInt(h, 16);
            if (isNaN(n)) return rgbFn(0, 0, 0);
            return rgbFn(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
        }

        /* pdf-coords maps a placement expressed in RENDERED (rotated) page
           fractions into unrotated MediaBox coordinates, and it needs the
           rotation the viewer applied to do it. Passing 0 here — as this did
           until 2026-08-10 — silently puts every annotation on a rotated page
           in the wrong place, at the wrong angle, with no error anywhere. */
        function pageInfoOf(page) {
            var turn = ((page.baseRotate || 0) + (page.turn || 0)) % 360;
            return { mediaW: page.width, mediaH: page.height, pdfRotate: ((turn % 360) + 360) % 360 };
        }

        /* ── text layout ────────────────────────────────────────────────── */

        /* Width of one run. A face that is somehow not embedded must NOT
           measure as zero: zero width means nothing ever exceeds maxWidth, so
           the text silently stops wrapping and runs off the page — the same
           "plausible number instead of an error" failure the font core exists
           to kill. Fall back to a conservative average advance so the line
           still breaks in roughly the right place. */
        /* Regular and bold are two different EMBEDDED FILES of the same face, so
           the map that holds them needs a key that says which. It was keyed by
           the bare faceId at the store and read by the bare faceId at the draw,
           while collectFaces already distinguished the two — so a document with
           both weights embedded them both and then drew everything with
           whichever landed last, and the Bold control was measurably a no-op
           (measured 2026-08-19: the bold and regular copies of one string came
           out at identical advance width, 94.58pt, sharing one font resource).

           The fallback is not cosmetic either: `drawText` skips a run whose font
           is missing, so a document whose ONLY text was bold would have written
           no visible text at all. */
        function fontKey(faceId, bold) {
            return faceId + (bold ? '|b' : '');
        }

        function pickFont(fontsFor, faceId, bold) {
            if (!faceId) return null;
            return fontsFor[fontKey(faceId, bold)] || fontsFor[faceId] || null;
        }

        function runWidth(run, size, fontsFor, bold) {
            var f = pickFont(fontsFor, run.faceId, bold);
            if (f) return f.widthOfTextAtSize(run.text, size);
            return run.text.length * size * 0.6;
        }

        /* Greedy wrap at `maxWidth` points. Measurement uses the run's own font,
           so a mixed-script line wraps where it actually overflows rather than
           where a single wrong font thought it would. */
        function layoutText(text, opts) {
            var fontsFor = opts.fontsFor || {};   // faceId -> pdf-lib font
            var size = opts.size;
            var maxWidth = opts.maxWidth;
            var lines = [];

            String(text || '').split(/\r?\n/).forEach(function (para) {
                if (!para) { lines.push([]); return; }
                var words = para.split(/(\s+)/);          // keep the spaces
                var line = [], lineWidth = 0;

                words.forEach(function (word) {
                    if (!word) return;
                    var runs = Fonts.planRuns(word, opts.faceId);
                    var wordWidth = 0;
                    runs.forEach(function (r) { wordWidth += runWidth(r, size, fontsFor, opts.bold); });
                    var isSpace = /^\s+$/.test(word);
                    if (!isSpace && lineWidth + wordWidth > maxWidth && line.length) {
                        lines.push(line);
                        line = []; lineWidth = 0;
                    }
                    if (isSpace && !line.length) return;   // no leading spaces on a wrapped line
                    runs.forEach(function (r) { if (r.faceId) line.push({ text: r.text, faceId: r.faceId }); });
                    lineWidth += wordWidth;
                });
                lines.push(line);
            });
            return lines;
        }

        /* Every face any run of this object needs, embedded once per document. */
        function collectFaces(objects) {
            var ids = {};
            objects.forEach(function (o) {
                if (o.type !== 'text' || !o.text) return;
                Fonts.planRuns(o.text, o.faceId).forEach(function (r) {
                    if (!r.faceId) return;
                    var k = fontKey(r.faceId, o.bold);
                    ids[k] = { id: r.faceId, bold: !!o.bold, key: k };
                });
            });
            return Object.keys(ids).map(function (k) { return ids[k]; });
        }

        /* ── object drawing ─────────────────────────────────────────────── */

        function drawText(pdfPage, obj, page, fontsFor) {
            var info = pageInfoOf(page);
            var box = Coords.placementToPdf(obj.placement, info);
            var size = obj.size || 14;
            var lineHeight = size * (obj.lineHeight || 1.25);
            var color = hexToRgb(obj.color);

            var lines = layoutText(obj.text, {
                fontsFor: fontsFor, size: size, maxWidth: box.width, faceId: obj.faceId,
                bold: !!obj.bold,
            });

            // Lay the text out in the BOX's own frame — x along its width from
            // the left edge, y up from its bottom edge — and carry every run's
            // origin through the box's rotation (Coords.boxPoint). Each run is
            // its own drawText (a word, a space, a script change), and pdf-lib
            // turns a run only about that run's start; until 2026-10-22 the
            // offsets between runs and lines were added along the PAGE axes, so
            // a rotated box — and any text at all on a turned page — came out
            // as words stepping sideways across the page (chat 708).
            var ly = box.height - size;   // first baseline: one size below the top
            lines.forEach(function (line) {
                var lineWidth = 0;
                line.forEach(function (seg) { lineWidth += runWidth(seg, size, fontsFor, obj.bold); });
                var lx = 0;
                if (obj.align === 'center') lx = (box.width - lineWidth) / 2;
                else if (obj.align === 'right') lx = box.width - lineWidth;

                line.forEach(function (seg) {
                    var f = pickFont(fontsFor, seg.faceId, obj.bold);
                    if (!f) return;
                    var at = Coords.boxPoint(box, lx, ly);
                    pdfPage.drawText(seg.text, {
                        x: at.x, y: at.y, size: size, font: f, color: color,
                        rotate: degrees(box.rotateDeg),
                        opacity: obj.opacity === undefined ? 1 : obj.opacity,
                    });
                    lx += f.widthOfTextAtSize(seg.text, size);
                });
                ly -= lineHeight;
            });
        }

        function drawRectLike(pdfPage, obj, page) {
            var box = Coords.placementToPdf(obj.placement, pageInfoOf(page));
            var common = {
                x: box.x, y: box.y, width: box.width, height: box.height,
                rotate: degrees(box.rotateDeg),
                opacity: obj.opacity === undefined ? 1 : obj.opacity,
            };
            if (obj.fill) { common.color = hexToRgb(obj.fill); }
            if (obj.stroke) {
                common.borderColor = hexToRgb(obj.stroke);
                common.borderWidth = obj.strokeWidth || 1;
                common.borderOpacity = obj.opacity === undefined ? 1 : obj.opacity;
            }
            if (obj.type === 'ellipse') {
                // pdf-lib turns an ellipse about its CENTRE (a rectangle about
                // its anchor), so the centre is the box's own centre carried
                // through the rotation — not anchor + half size along the page.
                var centre = Coords.boxPoint(box, box.width / 2, box.height / 2);
                pdfPage.drawEllipse({
                    x: centre.x, y: centre.y,
                    xScale: box.width / 2, yScale: box.height / 2,
                    color: obj.fill ? hexToRgb(obj.fill) : undefined,
                    borderColor: obj.stroke ? hexToRgb(obj.stroke) : undefined,
                    borderWidth: obj.strokeWidth || 1,
                    opacity: obj.opacity === undefined ? 1 : obj.opacity,
                    rotate: degrees(box.rotateDeg),
                });
            } else {
                pdfPage.drawRectangle(common);
            }
        }

        /* Highlight is a multiply-blended bar so the text under it stays
           readable — a plain opaque rectangle would erase what it marks. */
        function drawHighlight(pdfPage, obj, page) {
            var box = Coords.placementToPdf(obj.placement, pageInfoOf(page));
            pdfPage.drawRectangle({
                x: box.x, y: box.y, width: box.width, height: box.height,
                color: hexToRgb(obj.color || '#ffe600'),
                opacity: obj.opacity === undefined ? 0.4 : obj.opacity,
                blendMode: 'Multiply',
                rotate: degrees(box.rotateDeg),
            });
        }

        /* Freehand ink and straight lines share a path: points are stored as
           page fractions so they survive zoom exactly like every other object. */
        function drawInk(pdfPage, obj, page) {
            var pts = obj.points || [];
            if (pts.length < 2) return;
            var info = pageInfoOf(page);
            var color = hexToRgb(obj.color || '#e11d48');
            var w = obj.strokeWidth || 2;
            for (var i = 1; i < pts.length; i++) {
                var a = Coords.placementToPdf({ nx: pts[i - 1][0], ny: pts[i - 1][1], nw: 0, nh: 0, rot: 0 }, info);
                var b = Coords.placementToPdf({ nx: pts[i][0], ny: pts[i][1], nw: 0, nh: 0, rot: 0 }, info);
                pdfPage.drawLine({
                    start: { x: a.x, y: a.y }, end: { x: b.x, y: b.y },
                    thickness: w, color: color,
                    opacity: obj.opacity === undefined ? 1 : obj.opacity,
                });
            }
        }

        /* Embed the pictures the file SHOWS, each set of bytes once. Until
           27.10.2026 every entry of imageBytes went in: a signature placed and
           then deleted still rode in the saved file — invisible on the page,
           extractable from it (17 KB of someone's signature) — and «на всех
           страницах» carried one copy per page (4 pages: 66 KB instead of 17).
           Copies of one signature share one buffer (placeSignatureOnAllPages),
           so the buffer is the identity. */
        function embedShownImages(out, state, visible, imgBytes, chain) {
            imgBytes = imgBytes || {};
            var imagesFor = {}, shown = {};
            var byBytes = typeof Map === 'function' ? new Map() : null;
            visible.forEach(function (p) { shown[p.id] = true; });
            state.objects.forEach(function (o) {
                if ((o.type !== 'image' && o.type !== 'signature') || !shown[o.page]) return;
                var rec = imgBytes[o.id];
                if (!rec || !rec.bytes) return;
                chain = chain.then(function () {
                    var same = byBytes && byBytes.get(rec.bytes);
                    if (same) { imagesFor[o.id] = same; return; }
                    var embed = /png/i.test(rec.mime) ? out.embedPng(rec.bytes) : out.embedJpg(rec.bytes);
                    return Promise.resolve(embed).then(function (img) {
                        imagesFor[o.id] = img;
                        if (byBytes) byBytes.set(rec.bytes, img);
                    });
                });
            });
            return { chain: chain, imagesFor: imagesFor };
        }

        function drawImage(pdfPage, obj, page, imagesFor) {
            var img = imagesFor[obj.id];
            if (!img) return;
            var box = Coords.placementToPdf(obj.placement, pageInfoOf(page));
            pdfPage.drawImage(img, {
                x: box.x, y: box.y, width: box.width, height: box.height,
                rotate: degrees(box.rotateDeg),
                opacity: obj.opacity === undefined ? 1 : obj.opacity,
            });
        }

        /* ── main ───────────────────────────────────────────────────────── */

        /*
         * build(state, srcBytes, opts) -> Promise<Uint8Array>
         *
         * opts.externalDocs : { key -> Uint8Array } for imported pages
         * opts.imageBytes   : { objectId -> {bytes, mime} }
         * opts.onProgress   : (done, total) => void
         */
        /*
         * Does this session only ADD to the document, leaving its page set
         * exactly as it arrived?
         *
         * It matters because `copyPages` into a fresh document carries the page
         * CONTENT and nothing else: the AcroForm, bookmarks, attachments and
         * existing annotations are all left behind. Measured 2026-08-10 — a PDF
         * with form fields came back with the fields GONE even when the user
         * had only added a signature. Silent destruction of the part of the
         * document the user cared most about.
         *
         * So when the pages are untouched we mutate the ORIGINAL document and
         * everything survives. Only a real page-set change (delete, reorder,
         * insert, import, redaction raster) forces the rebuild, and then the
         * caller is told what that costs.
         */
        function pageSetUnchanged(state, opts) {
            if (opts.pageRasters && Object.keys(opts.pageRasters).length) return false;
            var source = state.pages.filter(function (p) { return !p.inserted; });
            if (state.order.length !== source.length) return false;
            for (var i = 0; i < state.order.length; i++) {
                var p = state.pages.filter(function (x) { return x.id === state.order[i]; })[0];
                if (!p || p.inserted || p.external) return false;
                if (p.src !== i) return false;          // reordered
            }
            return true;
        }

        function build(state, srcBytes, opts) {
            opts = opts || {};
            var PDFDocument = PDFLib.PDFDocument;

            var externalBytes = opts.externalDocs || {};
            var keepOriginal = pageSetUnchanged(state, opts);

            return PDFDocument.load(srcBytes, { ignoreEncryption: true }).then(function (src) {
                if (keepOriginal) return buildInPlace(state, src, opts);
                if (opts.onRebuild && hasForm(src)) opts.onRebuild('form');
                return PDFDocument.create().then(function (out) {
                    var visible = state.order.map(function (id) {
                        for (var i = 0; i < state.pages.length; i++) {
                            if (state.pages[i].id === id) return state.pages[i];
                        }
                        return null;
                    }).filter(Boolean);

                    // 1. copy the source pages we still show, in the shown order
                    var srcIndices = visible.filter(function (p) { return p.src !== null && !p.external; })
                                            .map(function (p) { return p.src; });
                    return out.copyPages(src, srcIndices).then(function (copied) {
                        var bySrc = {};
                        srcIndices.forEach(function (si, i) { bySrc[si] = copied[i]; });

                        var rasters = opts.pageRasters || {};
                        var pdfPages = [];
                        var rasterJobs = [];
                        var externalJobs = [];   // pages imported from another file
                        visible.forEach(function (p) {
                            var pdfPage;
                            if (rasters[p.id]) {
                                // REDACTED PAGE. The point of redaction is that
                                // the characters are GONE, not hidden behind a
                                // black box that any text extraction reads
                                // straight through. A PDF has no "delete these
                                // glyphs" operation reachable from a browser, so
                                // the page is replaced by a rendering of itself
                                // with the bars already burnt in. The text layer
                                // of that page dies with it — an explicit,
                                // user-visible trade, never a silent one.
                                var box = rasters[p.id];
                                pdfPage = out.addPage([box.pageW || p.width, box.pageH || p.height]);
                                rasterJobs.push({ page: pdfPage, raster: rasters[p.id] });
                            } else if (p.src !== null && !p.external && bySrc[p.src]) {
                                pdfPage = out.addPage(bySrc[p.src]);
                            } else if (p.external && externalBytes[p.external.docKey]) {
                                // Placeholder now, real content spliced in below:
                                // copyPages is async and page ORDER must not
                                // depend on which import resolves first.
                                pdfPage = out.addPage([p.width, p.height]);
                                externalJobs.push({ index: pdfPages.length, page: p });
                            } else {
                                pdfPage = out.addPage([p.width, p.height]);
                            }
                            // The user's turn composes on top of whatever the
                            // source page already declared.
                            var base = 0;
                            try { base = pdfPage.getRotation().angle || 0; } catch (e) { base = 0; }
                            if (p.turn) pdfPage.setRotation(degrees((base + p.turn) % 360));
                            pdfPages.push(pdfPage);
                        });

                        // 1a. splice in pages imported from other documents,
                        // replacing the placeholders IN PLACE so the order the
                        // user arranged survives regardless of load timing.
                        var chainExt = Promise.resolve();
                        var extDocs = {};
                        externalJobs.forEach(function (job) {
                            chainExt = chainExt.then(function () {
                                var key = job.page.external.docKey;
                                var load = extDocs[key]
                                    ? Promise.resolve(extDocs[key])
                                    : PDFDocument.load(externalBytes[key], { ignoreEncryption: true })
                                        .then(function (d) { extDocs[key] = d; return d; });
                                return load.then(function (doc) {
                                    return out.copyPages(doc, [job.page.external.index]);
                                }).then(function (copied) {
                                    out.removePage(job.index);
                                    out.insertPage(job.index, copied[0]);
                                    pdfPages[job.index] = out.getPage(job.index);
                                });
                            });
                        });

                        // 1b. draw the redacted-page rasters
                        var chain0 = chainExt;
                        rasterJobs.forEach(function (job) {
                            chain0 = chain0.then(function () {
                                var embed = /png/i.test(job.raster.mime)
                                    ? out.embedPng(job.raster.bytes)
                                    : out.embedJpg(job.raster.bytes);
                                return Promise.resolve(embed).then(function (img) {
                                    var sz = job.page.getSize();
                                    job.page.drawImage(img, { x: 0, y: 0, width: sz.width, height: sz.height });
                                });
                            });
                        });

                        // 2. embed every face the text objects need, once
                        var needed = collectFaces(state.objects);
                        var fontsFor = {};
                        var chain = chain0;
                        needed.forEach(function (n) {
                            chain = chain.then(function () {
                                return Fonts.embed(out, n.id, { bold: n.bold, subset: true })
                                    .then(function (f) { fontsFor[n.key] = f; });
                            });
                        });

                        // 3. embed the images the file shows
                        var emb = embedShownImages(out, state, visible, opts.imageBytes, chain);
                        var imagesFor = emb.imagesFor;
                        chain = emb.chain;

                        // 4. draw
                        return chain.then(function () {
                            visible.forEach(function (p, i) {
                                var pdfPage = pdfPages[i];
                                var objs = state.objects
                                    .filter(function (o) { return o.page === p.id; })
                                    .sort(function (a, b) { return a.z - b.z; });

                                objs.forEach(function (o) {
                                    // Already burnt into the page raster — drawing
                                    // it again would double-stamp the bar.
                                    if (o.type === 'redact' && rasters[p.id]) return;
                                    switch (o.type) {
                                        case 'text':      drawText(pdfPage, o, p, fontsFor); break;
                                        case 'image':
                                        case 'signature': drawImage(pdfPage, o, p, imagesFor); break;
                                        case 'rect':
                                        case 'ellipse':   drawRectLike(pdfPage, o, p); break;
                                        case 'whiteout':  drawRectLike(pdfPage, { ...o, fill: o.fill || '#ffffff', opacity: 1 }, p); break;
                                        // A redact object on a page we did NOT
                                        // rasterise is a bar over live text. That
                                        // is a cover, not a redaction, and the UI
                                        // refuses to reach here — but if it ever
                                        // does, draw it opaque rather than skip it.
                                        case 'redact':    drawRectLike(pdfPage, { ...o, fill: o.fill || '#000000', opacity: 1 }, p); break;
                                        case 'highlight': drawHighlight(pdfPage, o, p); break;
                                        case 'ink':
                                        case 'line':      drawInk(pdfPage, o, p); break;
                                        default: break;
                                    }
                                });
                                if (opts.onProgress) opts.onProgress(i + 1, visible.length);
                            });

                            return addNewFields(out, state, opts).then(function () {
                            return applyFormValues(out, opts);
                            }).then(function () {
                            if (opts.metadata) {
                                var m = opts.metadata;
                                if (m.title !== undefined) out.setTitle(String(m.title || ''));
                                if (m.author !== undefined) out.setAuthor(String(m.author || ''));
                                if (m.subject !== undefined) out.setSubject(String(m.subject || ''));
                                if (m.keywords !== undefined) {
                                    out.setKeywords(String(m.keywords || '').split(/\s*,\s*/).filter(Boolean));
                                }
                            }
                            return out.save({ useObjectStreams: opts.objectStreams !== false });
                            });
                        });
                    });
                });
            });
        }

        /*
         * Write the user's answers into the document's REAL form fields.
         *
         * Stamping the text over the box would look identical on screen and be
         * wrong in every way that counts: the field stays empty, so the form
         * cannot be re-opened for editing and any system that reads field
         * values (a portal, an accounting import) sees a blank application
         * beneath a page that appears filled in.
         *
         * ⚠️ `updateFieldAppearances()` defaults to Helvetica — WinAnsi — and
         * THROWS on «Иванов». The covering face is embedded first and handed
         * in, or a Russian form would fail at save with a font error that
         * mentions nothing about forms.
         */
        function applyFormValues(out, opts) {
            var values = opts.formValues || {};
            var names = Object.keys(values);
            if (!names.length) return Promise.resolve();

            var form;
            try { form = out.getForm(); } catch (e) { return Promise.resolve(); }

            var faceId = opts.formFaceId || 'noto';
            return Fonts.embed(out, faceId, { subset: true }).then(function (font) {
                names.forEach(function (name) {
                    var v = values[name];
                    try {
                        var field = form.getField(name);
                        // ⚠️ Duck-typing, NOT `field.constructor.name`. The
                        // browser loads a MINIFIED pdf-lib where every class
                        // name is mangled, so a name check silently matches
                        // nothing and checkboxes and dropdowns stay unset —
                        // while the node tests pass, because the npm build is
                        // unminified. Methods survive minification; names do not.
                        if (typeof field.check === 'function' && typeof field.uncheck === 'function') {
                            if (v && v !== 'Off' && v !== 'false' && v !== '0') field.check();
                            else field.uncheck();
                        } else if (typeof field.select === 'function') {
                            if (String(v)) field.select(String(v));   // dropdown, option list, radio
                        } else if (typeof field.setText === 'function') {
                            field.setText(String(v));
                        }
                    } catch (e) {
                        // A field that vanished between opening and saving is
                        // the user's document changing under us, not a crash.
                        if (opts.onFieldError) opts.onFieldError(name, e);
                    }
                });
                try { form.updateFieldAppearances(font); } catch (e) {
                    if (opts.onFieldError) opts.onFieldError('*appearances*', e);
                }
                // Flattening would make the form permanently unfillable; the
                // user asked to FILL it, not to freeze it.
                if (opts.flattenForm) { try { form.flatten(); } catch (e) {} }
            });
        }

        /*
         * NEW form fields and links, created from objects the user drew.
         *
         * Both are real PDF constructs, not drawings: a field has a name and a
         * value a recipient can fill, a link is an annotation a reader can
         * click. Drawing a blue rectangle and calling it a link is the version
         * of this that looks right and does nothing.
         *
         * ⚠️ pdf-lib can create text fields, checkboxes, dropdowns, option
         * lists and radio groups — but NOT signature fields. Offering one would
         * be promising a box that no reader can sign.
         */
        function addNewFields(out, state, opts) {
            var wanted = state.objects.filter(function (o) { return o.type === 'formfield' || o.type === 'link'; });
            if (!wanted.length) return Promise.resolve();

            var pages = out.getPages();
            var order = state.order;
            var form = null;
            try { form = out.getForm(); } catch (e) { form = null; }

            var faceId = opts.formFaceId || 'noto';
            /* A radio GROUP is one field with several buttons, so the group is
               created once per name and every button is added to it. Keyed
               across the whole pass because the buttons of one question may sit
               on different pages. */
            var radioGroups = {};
            return Fonts.embed(out, faceId, { subset: true }).then(function (font) {
                wanted.forEach(function (o) {
                    var idx = order.indexOf(o.page);
                    var pdfPage = pages[idx];
                    if (!pdfPage) return;
                    var page = null;
                    for (var i = 0; i < state.pages.length; i++) if (state.pages[i].id === o.page) page = state.pages[i];
                    if (!page) return;

                    var box = Coords.placementToPdf(o.placement, pageInfoOf(page));
                    if (o.type === 'link') { addLink(out, pdfPage, box, o.url); return; }
                    if (!form) return;
                    var at = widgetPlacement(box);

                    try {
                        var name = String(o.fieldName || '').trim() || ('field_' + o.id);
                        var f;
                        if (o.fieldKind === 'checkbox') {
                            f = form.createCheckBox(name);
                            f.addToPage(pdfPage, at);
                            if (o.fieldValue && o.fieldValue !== 'Off') f.check();
                        } else if (o.fieldKind === 'dropdown') {
                            f = form.createDropdown(name);
                            var optsList = (o.fieldOptions || []).filter(Boolean);
                            if (optsList.length) f.addOptions(optsList);
                            f.addToPage(pdfPage, Object.assign({ font: font }, at));
                            if (o.fieldValue) { try { f.select(String(o.fieldValue)); } catch (e) {} }
                        } else if (o.fieldKind === 'radio') {
                            // Same name = same question. The buttons are the
                            // options; selecting one clears the others, which is
                            // the whole point and is impossible with checkboxes.
                            var option = String((o.fieldOptions && o.fieldOptions[0]) || o.fieldValue || name);
                            f = radioGroups[name];
                            if (!f) { f = form.createRadioGroup(name); radioGroups[name] = f; }
                            f.addOptionToPage(option, pdfPage, at);
                            if (o.fieldValue === 'on') { try { f.select(option); } catch (e) {} }
                        } else {
                            f = form.createTextField(name);
                            // A multiline field wraps and keeps line breaks; a
                            // single-line one silently drops everything after the
                            // first return, which is not what an address or a
                            // comment box is for.
                            if (o.fieldKind === 'textarea') { try { f.enableMultiline(); } catch (e) {} }
                            f.setText(String(o.fieldValue || ''));
                            f.addToPage(pdfPage, Object.assign({ font: font }, at));
                        }
                    } catch (e) {
                        // A duplicate name is the common cause, and it is the
                        // user's to fix — losing the rest of the export over it
                        // would not help them.
                        if (opts.onFieldError) opts.onFieldError(o.fieldName || o.id, e);
                    }
                });
                if (form) { try { form.updateFieldAppearances(font); } catch (e) {} }
            });
        }

        /* A form widget turns only by a right angle (/MK /R must be a multiple
           of 90, pdf-lib asserts it) and pdf-lib places it about the same
           anchor placementToPdf returns — so a field on a turned page gets the
           page's turn, and reads upright on screen, instead of a rectangle
           measured along the wrong axes (on a page turned 180° the widget left
           the page altogether, measured 2026-10-22). A box the user turned by
           some other angle keeps its centre and takes the nearest right angle:
           the editor only offers right angles for fields, this is the guard. */
        function widgetPlacement(box) {
            var q = (Math.round((box.rotateDeg || 0) / 90) * 90) % 360;
            var centre = Coords.boxPoint(box, box.width / 2, box.height / 2);
            var upright = { x: 0, y: 0, width: box.width, height: box.height, rotateDeg: q };
            var off = Coords.boxPoint(upright, box.width / 2, box.height / 2);
            return {
                x: centre.x - off.x, y: centre.y - off.y,
                width: box.width, height: box.height, rotate: degrees(q),
            };
        }

        /* A link is a /Link annotation with a URI action. pdf-lib has no helper,
           so the dictionary is built directly — the same structure a reader
           expects, not a rectangle that merely looks clickable. */
        function addLink(out, pdfPage, box, url) {
            // /Rect is upright in unrotated page space whatever the page's
            // /Rotate or the box's own turn; the anchor's [x, y, x+w, y+h] was
            // that only on an upright page — on a turned one the clickable
            // area lay beside the box the user drew (measured 2026-10-22).
            var r = Coords.boxBounds(box);
            var href = String(url || '').trim();
            if (!href) return;
            // Only schemes a reader will actually follow; `javascript:` in a
            // document someone else opens is not a link, it is a payload.
            if (!/^(https?:\/\/|mailto:|tel:)/i.test(href)) return;
            try {
                var ctx = out.context;
                var annot = ctx.obj({
                    Type: 'Annot',
                    Subtype: 'Link',
                    Rect: [r.x, r.y, r.x + r.width, r.y + r.height],
                    Border: [0, 0, 0],
                    A: ctx.obj({ Type: 'Action', S: 'URI', URI: PDFLib.PDFString.of(href) }),
                });
                var ref = ctx.register(annot);
                var existing = pdfPage.node.Annots();
                if (existing) existing.push(ref);
                else pdfPage.node.set(PDFLib.PDFName.of('Annots'), ctx.obj([ref]));
            } catch (e) { /* a link that cannot be written must not lose the file */ }
        }

        function hasForm(doc) {
            try { return doc.getForm().getFields().length > 0; } catch (e) { return false; }
        }

        /* The preserving path: draw onto the document that arrived, so its form,
           bookmarks, attachments and existing annotations all survive. */
        function buildInPlace(state, out, opts) {
            var visible = state.order.map(function (id) {
                for (var i = 0; i < state.pages.length; i++) if (state.pages[i].id === id) return state.pages[i];
                return null;
            }).filter(Boolean);

            var pdfPages = out.getPages();
            visible.forEach(function (p, i) {
                if (!p.turn || !pdfPages[i]) return;
                var base = 0;
                try { base = pdfPages[i].getRotation().angle || 0; } catch (e) { base = 0; }
                pdfPages[i].setRotation(degrees((base + p.turn) % 360));
            });

            var needed = collectFaces(state.objects);
            var fontsFor = {};
            var chain = Promise.resolve();
            needed.forEach(function (n) {
                chain = chain.then(function () {
                    return Fonts.embed(out, n.id, { bold: n.bold, subset: true })
                        .then(function (f) { fontsFor[n.key] = f; });
                });
            });

            var emb = embedShownImages(out, state, visible, opts.imageBytes, chain);
            var imagesFor = emb.imagesFor;
            chain = emb.chain;

            return chain.then(function () {
                visible.forEach(function (p, i) {
                    var pdfPage = pdfPages[i];
                    if (!pdfPage) return;
                    state.objects
                        .filter(function (o) { return o.page === p.id; })
                        .sort(function (a, b) { return a.z - b.z; })
                        .forEach(function (o) { drawObject(pdfPage, o, p, fontsFor, imagesFor); });
                    if (opts.onProgress) opts.onProgress(i + 1, visible.length);
                });
                return addNewFields(out, state, opts).then(function () {
                    return applyFormValues(out, opts);
                }).then(function () {
                    writeMetadata(out, opts);
                    return out.save({ useObjectStreams: opts.objectStreams !== false });
                });
            });
        }

        function drawObject(pdfPage, o, p, fontsFor, imagesFor) {
            switch (o.type) {
                case 'text':      drawText(pdfPage, o, p, fontsFor); break;
                case 'image':
                case 'signature': drawImage(pdfPage, o, p, imagesFor); break;
                case 'rect':
                case 'ellipse':   drawRectLike(pdfPage, o, p); break;
                case 'whiteout':  drawRectLike(pdfPage, Object.assign({}, o, { fill: o.fill || '#ffffff', opacity: 1 }), p); break;
                case 'redact':    drawRectLike(pdfPage, Object.assign({}, o, { fill: o.fill || '#000000', opacity: 1 }), p); break;
                case 'highlight': drawHighlight(pdfPage, o, p); break;
                case 'ink':
                case 'line':      drawInk(pdfPage, o, p); break;
                default: break;
            }
        }

        function writeMetadata(out, opts) {
            if (!opts.metadata) return;
            var m = opts.metadata;
            if (m.title !== undefined) out.setTitle(String(m.title || ''));
            if (m.author !== undefined) out.setAuthor(String(m.author || ''));
            if (m.subject !== undefined) out.setSubject(String(m.subject || ''));
            if (m.keywords !== undefined) {
                out.setKeywords(String(m.keywords || '').split(/\s*,\s*/).filter(Boolean));
            }
        }

        return { build: build, layoutText: layoutText, collectFaces: collectFaces,
                 runWidth: runWidth, applyFormValues: applyFormValues,
                 pageSetUnchanged: pageSetUnchanged, addNewFields: addNewFields };
    }

    return { makeExporter: makeExporter };
});
